import { ApiError } from "@/lib/apiClient";
import { Nullable } from "@/types";
import { Product } from "@/types/models/product";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type wholesalerProductsState = {
  byWholesalerId: {
    [id: string]: {
      data: Product[];
      loading: boolean;
      error: Nullable<ApiError>;
      pagination: {
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      };
    };
  };
  selectedProductIds: { [id: string]: Product };
};
const initialState: wholesalerProductsState = {
  byWholesalerId: {} as {
    [id: string]: {
      data: Product[];
      loading: boolean;
      error: Nullable<ApiError>;
      pagination: {
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      };
    };
  },
  selectedProductIds: {} as { [id: string]: Product },
};

const wholesalerProductsSlice = createSlice({
  name: "wholesalerProducts",
  initialState,
  reducers: {
    fetchWholesalerProductRequest: (
      state,
      action: PayloadAction<{ id: string; page: number; pageSize: number }>,
    ) => {
      state.byWholesalerId[action.payload.id] = {
        loading: true,
        error: null,
        data: [],
        pagination: {
          page: action.payload.page,
          total: 0,
          pageSize: action.payload.pageSize,
          totalPages: 0,
        },
      };
    },
    fetchWholesalerProductSuccess: (
      state,
      action: PayloadAction<{
        id: string;
        data: Product[];
        pagination: {
          page: number;
          total: number;
          pageSize: number;
          totalPages: number;
        };
      }>,
    ) => {
      state.byWholesalerId[action.payload.id] = {
        data: action.payload.data,
        loading: false,
        error: null,
        pagination: {
          page: action.payload.pagination.page,
          total: action.payload.pagination.total,
          pageSize: action.payload.pagination.pageSize,
          totalPages: action.payload.pagination.totalPages,
        },
      };
    },
    fetchWholesalerProductFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },

    // attach wholesaler product
    attachWholesalerProductRequest: (
      state,
      action: PayloadAction<{
        id: string;
        product_ids: string[];
        margin: number;
        margin_type: string;
        payment_type: string[];
      }>,
    ) => {
      if (!action.payload.id || !action.payload.product_ids) return;
      state.byWholesalerId[action.payload.id].loading = true;
      state.byWholesalerId[action.payload.id].error = null;
    },
    attachWholesalerProductSuccess: (
      state,
      action: PayloadAction<{ id: string; data: string[] }>,
    ) => {
      const products = state.byWholesalerId[action.payload.id];
      if (!products) return;
      const data = products.data.map((item) => {
        if (!item.id) return item;
        return action.payload.data.includes(item.id)
          ? { ...item, already_mapped: true }
          : item;
      });
      products.data = data;
      state.byWholesalerId[action.payload.id].loading = false;
      state.byWholesalerId[action.payload.id].error = null;
      state.byWholesalerId[action.payload.id] = products;
      state.selectedProductIds = {};
    },
    attachWholesalerProductFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },

    // detach wholesaler product
    detachWholesalerProductRequest: (
      state,
      action: PayloadAction<{
        id: string;
        product_ids: string[];
      }>,
    ) => {
      if (!action.payload.id || !action.payload.product_ids) return;
      state.byWholesalerId[action.payload.id].loading = true;
      state.byWholesalerId[action.payload.id].error = null;
    },
    detachWholesalerProductSuccess: (
      state,
      action: PayloadAction<{ id: string; data: string[] }>,
    ) => {
      const products = state.byWholesalerId[action.payload.id];
      if (!products) return;
      const data = products.data.map((item) => {
        if (!item.id) return item;
        return action.payload.data.includes(item.id)
          ? { ...item, already_mapped: false }
          : item;
      });
      products.data = data;
      state.byWholesalerId[action.payload.id].loading = false;
      state.byWholesalerId[action.payload.id].error = null;
      state.byWholesalerId[action.payload.id] = products;
    },
    detachWholesalerProductFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },

    // selected product ids
    setSelectedProductIds: (state, action: PayloadAction<Product[]>) => {
      for (const product of action.payload) {
        if (product.id && !product.already_mapped) {
          state.selectedProductIds[product.id] = product;
        }
      }
    },
    removeSelectedProductIds: (state, action: PayloadAction<string[]>) => {
      for (const product of action.payload) {
        if (product) {
          delete state.selectedProductIds[product];
        }
      }
    },
  },
});

export const {
  fetchWholesalerProductFailure,
  fetchWholesalerProductRequest,
  fetchWholesalerProductSuccess,
  setSelectedProductIds,
  removeSelectedProductIds,
  attachWholesalerProductFailure,
  attachWholesalerProductRequest,
  attachWholesalerProductSuccess,
  detachWholesalerProductFailure,
  detachWholesalerProductRequest,
  detachWholesalerProductSuccess,
} = wholesalerProductsSlice.actions;

export default wholesalerProductsSlice.reducer;
